import { ChevronUpIcon } from '@chakra-ui/icons'
import { Button, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react'

import {
  useInterview,
  useUpdateInterview,
} from '@/react-query/interview/interview'
import { useIsViewOnlyMember } from '@/react-query/team'

type Decision = 'strong no' | 'no' | 'yes' | 'strong yes'
type DecisionSelectorProps = {
  defaultBackgroundColor?: string
  useFixedPositioning?: boolean
}

const DECISION_COLORS: Record<Decision, { bg: string; fg: string }> = {
  'strong no': { bg: '#C00', fg: 'white' },
  no: { bg: '#F60', fg: 'white' },
  yes: { bg: '#9C0', fg: 'black' },
  'strong yes': { bg: '#090', fg: 'white' },
}

export const DecisionSelector = ({
  defaultBackgroundColor,
  useFixedPositioning = false,
}: DecisionSelectorProps) => {
  const decision = useInterview().data?.decision as Decision | undefined
  const updateInterview = useUpdateInterview()
  const isViewOnlyUser = useIsViewOnlyMember()

  const bg =
    (decision && DECISION_COLORS[decision].bg) ?? defaultBackgroundColor
  const fg =
    (decision && DECISION_COLORS[decision].fg) ??
    (defaultBackgroundColor === '#ffffff' ? 'black' : undefined)

  const menuProps = useFixedPositioning
    ? {
        placement: 'bottom-end' as const,
        strategy: 'fixed' as const,
        modifiers: [
          { name: 'flip', options: { fallbackPlacements: ['top-end'] } },
        ],
      }
    : {}

  return (
    <Menu {...menuProps}>
      <MenuButton
        as={Button}
        rounded="1.25rem"
        rightIcon={isViewOnlyUser ? undefined : <ChevronUpIcon />}
        backgroundColor={bg}
        color={fg}
        variant="brand-solid-gray"
        _hover={{ backgroundColor: bg }}
        _active={{ backgroundColor: bg }}
        maxW="100%"
        minW="fit-content"
        h="auto"
        py={2}
        whiteSpace="normal"
      >
        {decision || 'yes or no'}
      </MenuButton>
      {!isViewOnlyUser && (
        <MenuList
          {...(useFixedPositioning
            ? {
                minW: 'unset',
                w: 'max-content',
                bg: 'transparent',
                boxShadow: 'none',
              }
            : {
                w: 'fit-content',
                background: '',
                boxShadow: '',
              })}
          borderWidth="0px"
          color="white"
          fontWeight="bold"
          p={0}
        >
          <MenuItem
            px={4}
            w="fit-content"
            rounded="1.25rem"
            backgroundColor="#C00"
            _hover={{ backgroundColor: '#C00' }}
            onClick={() => updateInterview.mutate({ decision: 'strong no' })}
            _first={{ py: 2 }}
          >
            strong no
          </MenuItem>

          <MenuItem
            px={4}
            py={2}
            w="fit-content"
            rounded="1.25rem"
            backgroundColor="#F60"
            _hover={{ backgroundColor: '#F60' }}
            onClick={() => updateInterview.mutate({ decision: 'no' })}
          >
            no
          </MenuItem>

          <MenuItem
            px={4}
            w="fit-content"
            rounded="1.25rem"
            backgroundColor="#9C0"
            _hover={{ backgroundColor: '#9C0' }}
            color="black"
            onClick={() => updateInterview.mutate({ decision: 'yes' })}
            py={2}
          >
            yes
          </MenuItem>

          <MenuItem
            px={4}
            w="fit-content"
            rounded="1.25rem"
            backgroundColor="#090"
            _hover={{ backgroundColor: '#090' }}
            onClick={() => updateInterview.mutate({ decision: 'strong yes' })}
            _last={{ py: 2 }}
          >
            strong yes
          </MenuItem>
        </MenuList>
      )}
    </Menu>
  )
}
